import {
  Box,
  Divider,
  Flex,
  ReportSection,
  ReportSectionItemContent,
  Text,
} from '@/components';
import type { ProjectLedgerData } from '@/features/project/projects/detail/ledger/_hooks/useProjectLedgerData';
import {
  ITEM_SPLITTER,
  findUpperRecursive,
  formatProjectEndYearForPdf,
  formatProjectStartYearForPdf,
} from '@/utils';
import {
  ImplementationMethodLabelModifier,
  ProjectCategoryLabelModifier,
} from '@/utils/enum';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_MIDDLE_CAPTION_WIDTH,
  type TableRowType,
  getEmptyTableDataByHeader,
  getLegalBaseTableData,
  getPlanTableData,
  getRelatedProjectNames,
} from '../../../utils';
import { LedgerTitle } from './LedgerTitle';
import { TableContent } from './TableContent';

export const BasicInfoSection: FC<{ ledgerData: ProjectLedgerData }> = ({
  ledgerData: {
    project,
    ministryName,
    organizationNameBySnapshot,
    businessManagerNameBySnapshot,
    otherOrganizationNameBySnapshot,
    data: {
      rs: { policies },
    },
  },
}) => {
  const { t } = useTranslation();

  const policyTableData = useMemo<TableRowType>(() => {
    const policyLevel1Width = LEDGER_MIDDLE_CAPTION_WIDTH - 1;
    const policyLevel2Width = `calc(50% - ${policyLevel1Width}px)`;
    const header: TableRowType[number] = {
      isHeader: true,
      row: [
        {
          text: t('policy-level-1'),
          cellStyle: {
            width: policyLevel1Width,
            flex: undefined,
          },
        },
        {
          text: t('policy-level-2'),
          cellStyle: {
            flex: undefined,
            width: `calc(50% - ${policyLevel1Width}px)`,
          },
        },
        { text: t('policy-level-3'), ratio: 1.03 },
        { text: t('policy-url'), ratio: 0.6 },
      ],
    };
    const data =
      project?.policiesForProject.map<TableRowType[number]>((policy) => {
        const currentPolicies = findUpperRecursive(
          policy.policyId,
          policies ?? [],
        ).reverse();
        return {
          row: [
            ...currentPolicies.map((v, i) => ({
              ...(i === 0 || i === 1
                ? {
                    cellStyle: {
                      width: i === 0 ? policyLevel1Width : policyLevel2Width,
                      flex: undefined,
                    },
                  }
                : {}),
              text: v.name.replace(/[\r\n\s]+/g, ''),
            })),
            { text: policy.policyUrl ?? '' },
          ].map((v, i) => ({ ...v, ratio: header.row[i]?.ratio })),
        };
      }) ?? [];
    return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
  }, [policies, project?.policiesForProject, t]);

  const legalBaseTableData = getLegalBaseTableData({ project });
  const planTableData = getPlanTableData({ project });

  const subsidyRateTableData = useMemo<TableRowType>(() => {
    const header: TableRowType[number] = {
      isHeader: true,
      row: [
        { text: t('subsidy-rate-target'), ratio: 1.5 },
        { text: t('subsidy-rate'), ratio: 1 },
        { text: t('subsidy-rate-limit-and-so-on'), ratio: 1 },
        { text: t('subsidy-rate-url'), ratio: 1 },
      ],
    };
    const data =
      project?.subsidyRates?.map<TableRowType[number]>((subsidyRate) => {
        return {
          row: [
            { text: subsidyRate.target },
            { text: subsidyRate.ratesText },
            { text: subsidyRate.limitText },
            { text: subsidyRate.url },
          ].map((v, i) => ({ ...v, ratio: header.row[i]?.ratio })),
        };
      }) ?? [];
    return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
  }, [project?.subsidyRates, t]);

  return (
    <ReportSection
      title={
        <>
          <Flex vertical gap={8}>
            <Flex gap={16}>
              <Text
                text={t('project-report-title', { year: project?.fiscalYear })}
                variant="footnote"
              />
              <Text text={ministryName} variant="footnote" />
            </Flex>
            <LedgerTitle projectId={project?.id} projectName={project?.name} />
          </Flex>
          <Divider />
          <Text text={t('basic-information')} />
        </>
      }
      items={[
        {
          subtitle: t('organization-information'),
          item: (
            <Flex vertical>
              <ReportSectionItemContent
                label={t('ministry')}
                value={ministryName}
              />
              <ReportSectionItemContent
                label={t('organization-in-charge')}
                value={organizationNameBySnapshot}
              />
              <ReportSectionItemContent
                label={t('business-manager')}
                value={businessManagerNameBySnapshot}
              />
              <ReportSectionItemContent
                label={t('other-organization-in-charge')}
                value={otherOrganizationNameBySnapshot}
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('basic-information'),
          item: (
            <Box>
              <Flex>
                <ReportSectionItemContent
                  label={t('project-number')}
                  value={project?.projectNumber}
                />
                <ReportSectionItemContent
                  label={t('start-fiscal-year-of-project')}
                  value={formatProjectStartYearForPdf(project?.startYear)}
                  hasLeftBorder
                />
                <ReportSectionItemContent
                  labelStyle={{ width: 150 }}
                  label={t('end-fiscal-year-of-project')}
                  value={formatProjectEndYearForPdf(project?.endYear)}
                  hasLeftBorder
                />
              </Flex>
              <Flex>
                <ReportSectionItemContent
                  label={t('fiscal-year')}
                  value={project?.fiscalYear}
                  isLast
                />
                <ReportSectionItemContent
                  label={t('project-category')}
                  value={
                    project?.category &&
                    ProjectCategoryLabelModifier[project.category]
                  }
                  isLast
                  hasLeftBorder
                />
              </Flex>
            </Box>
          ),
        },
        {
          subtitle: t('policy'),
          item: <TableContent noneBorder data={policyTableData} />,
        },
        {
          subtitle: t('related-projects'),
          item: (
            <Flex style={{ height: '100%' }}>
              <ReportSectionItemContent
                value={getRelatedProjectNames({
                  project,
                })}
                isLast
              />
              <ReportSectionItemContent
                hasLeftBorder
                label={t('main-cost')}
                value={project?.mainCosts
                  .map((d) => d.name)
                  .join(ITEM_SPLITTER)}
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('overview-and-purpose'),
          item: (
            <Box>
              <ReportSectionItemContent
                label={t('project-purpose')}
                value={project?.purpose}
              />
              <ReportSectionItemContent
                label={t('project-task')}
                value={project?.task}
              />
              <ReportSectionItemContent
                label={t('project-overview')}
                value={project?.overview}
              />
              <ReportSectionItemContent
                label={t('overview-url')}
                value={project?.overviewUrl}
                isLast
              />
            </Box>
          ),
        },
        {
          subtitle: t('legal-bases'),
          item: <TableContent noneBorder data={legalBaseTableData} />,
        },
        {
          subtitle: t('project-plan'),
          item: <TableContent noneBorder data={planTableData} />,
        },
        {
          subtitle: t('implementation-method'),
          item: (
            <ReportSectionItemContent
              value={(project?.implementationMethods ?? [])
                .map((value) => ImplementationMethodLabelModifier[value])
                .join(ITEM_SPLITTER)}
              isLast
            />
          ),
        },
        {
          subtitle: t('subsidy-rate-and-so-on'),
          item: <TableContent noneBorder data={subsidyRateTableData} />,
        },
        {
          subtitle: t('remark'),
          item: (
            <ReportSectionItemContent
              // wrapperStyle={{ height: '100%', borderLeft: LEDGER_BORDER }}
              value={project?.remark}
              isLast
            />
          ),
        },
      ]}
    />
  );
};
